import {Tag, Table, Modal} from 'letrus-ui';
import {ActivitiesComparison} from 'utils/types/newReviewed';
import {useMobile} from 'utils/hooks';
import {fetchReviewComparisonActivitiesService} from 'store/services/compositionServices';
import {useEffect, useState} from 'react';
import LetrusLoader from 'components/LetrusLoader';
import ScoreDifference from '../ScoreDifference';
import SuggestedImprovement from '../SuggestedImprovement';

import styles from './ComparisonModal.module.scss';
import CompareCompetenceList from '../CompareCompetenceList';

interface ComparisonModalProps {
  id: string;
  parentId?: number;
  reviewId?: number;
}

function ComparisonModal({id, parentId, reviewId}: ComparisonModalProps) {
  const [comparisonActivitiesResult, setComparisonActivitiesResult] =
    useState<ActivitiesComparison>();

  const {isMobile} = useMobile();

  function getReviewComparisonActivities(parentId?: number, reviewId?: number) {
    if (!parentId || !reviewId) return;

    fetchReviewComparisonActivitiesService({
      reviewParentId: parentId,
      reviewId,
    })
      .then((response) => {
        setComparisonActivitiesResult(response.data);
      })
      .catch(() => {
        // TODO: verify with design and product team the error feedback
      });
  }

  useEffect(() => {
    getReviewComparisonActivities(parentId, reviewId);
  }, [parentId, reviewId]);

  const firstActivity = comparisonActivitiesResult?.first_activity;
  const secondActivity = comparisonActivitiesResult?.second_activity;

  const competences =
    firstActivity && secondActivity
      ? Object.keys(firstActivity.review_grades).map((key) => {
          const firstCompetence = firstActivity.review_grades[key];
          const secondCompetence = secondActivity.review_grades[key];

          return {
            competence: firstCompetence.competence_name,
            firstPoints: firstCompetence.score,
            secondPoints: secondCompetence.score,
            suggested:
              secondCompetence.podemos_melhorar ||
              firstCompetence.podemos_melhorar ||
              '',
          };
        })
      : undefined;

  return (
    <Modal id={id}>
      {firstActivity && secondActivity && competences ? (
        <div className={styles.container}>
          {isMobile ? (
            <CompareCompetenceList competences={competences} />
          ) : (
            <>
              <section className={styles.header}>
                <span>
                  {firstActivity?.test_name}
                  <Tag
                    backgroundColor="#D9D9D9"
                    boxShadow="0px 4px 16px rgba(22, 22, 22, 0.1)"
                    mainColor="#D9D9D9"
                    text={new Date(
                      firstActivity.start_datetime,
                    ).toLocaleDateString()}
                    textColor="#000000"
                    variant="readOnly"
                  />
                </span>

                <span>
                  {secondActivity.test_name}
                  <Tag
                    backgroundColor="#D9D9D9"
                    boxShadow="0px 4px 16px rgba(22, 22, 22, 0.1)"
                    mainColor="#D9D9D9"
                    text="REESCRITA"
                    textColor="#000000"
                    variant="readOnly"
                  />
                </span>
              </section>

              <Table.Container className={styles.tableContainer}>
                <Table.Header
                  headings={[
                    'Competências',
                    '1º Atividade',
                    '2º Atividade',
                    'Diferença de Nota',
                    'Como Melhorar',
                  ]}
                />

                <tbody>
                  {competences.map((competences) => (
                    <tr key={competences.competence}>
                      <td>{competences.competence}</td>
                      <td>{competences.firstPoints}</td>
                      <td>{competences.secondPoints}</td>
                      <td>
                        <ScoreDifference
                          firstPoints={competences.firstPoints}
                          secondPoints={competences.secondPoints}
                        />
                      </td>
                      <td>
                        <SuggestedImprovement
                          suggestedImprovement={competences.suggested}
                        />
                      </td>
                    </tr>
                  ))}

                  <tr className={styles.total}>
                    <td>
                      <strong>TOTAL</strong>
                    </td>
                    <td>
                      <strong>{firstActivity.total_grade.grade}</strong>
                    </td>
                    <td>
                      <strong>{secondActivity.total_grade.grade}</strong>
                    </td>
                    <td>
                      <ScoreDifference
                        firstPoints={firstActivity.total_grade.grade}
                        secondPoints={secondActivity.total_grade.grade}
                      />
                    </td>
                    <td />
                  </tr>
                </tbody>
              </Table.Container>
            </>
          )}
        </div>
      ) : (
        <LetrusLoader />
      )}
    </Modal>
  );
}

export default ComparisonModal;
